import React from 'react'
import {
  MenuItem,
  Select,
  TextField,
  FormControl,
  InputLabel,
  Grid,
  Paper,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material'
import { Button } from 'reactstrap'
import DeleteButton from '../Buttons/DeleteButton'

/*
  A simple form designer component that allows users to create and edit form inputs.
  Users can add different types of inputs, set labels, and mark them as required.
  The designed form structure is displayed in a preview area.

  material-ui is used for UI components.

  Supported types of inputs supported: text, textarea, number, date, time, datetime, checkbox, select, multiselect, dropdown, radio, switch, email, phone, paragraph (static text), 

{
  type: 'text',
  label: 'Full Name',
  placeholder: 'Enter your full name',
  required: true,
  validation: [
    { type: 'required', message: 'Name is required' },
    { type: 'minLength', value: 2, message: 'Name must be at least 2 characters' }
  ]
}


{
  type: 'email',
  label: 'Email Address',
  placeholder: 'you@example.com',
  required: true
}

{
  type: 'number',
  label: 'Age',
  placeholder: 'Enter your age',
  properties: {
    min: 0,
    max: 120,
    step: 1
  }
}

{
  type: 'phone',
  label: 'Phone Number',
  placeholder: '+1 (555) 123-4567',
  required: true
}


{
  type: 'textarea',
  label: 'Message',
  placeholder: 'Enter your message here...',
  properties: {
    rows: 4,
    maxLength: 500
  }
}

{
  type: 'select',
  label: 'Country',
  placeholder: 'Select your country',
  options: [
    { label: 'United States', value: 'us' },
    { label: 'Canada', value: 'ca' },
    { label: 'United Kingdom', value: 'uk' }
  ],
  required: true
}

{
  type: 'multiselect',
  label: 'Skills',
  placeholder: 'Select your skills',
  options: [
    { label: 'JavaScript', value: 'js' },
    { label: 'TypeScript', value: 'ts' },
    { label: 'React', value: 'react' },
    { label: 'Node.js', value: 'node' }
  ]
}

{
  type: 'radio',
  label: 'Preferred Contact Method',
  options: [
    { label: 'Email', value: 'email' },
    { label: 'Phone', value: 'phone' },
    { label: 'SMS', value: 'sms' }
  ],
  required: true
}

{
  type: 'checkbox',
  label: 'Interests',
  options: [
    { label: 'Technology', value: 'tech' },
    { label: 'Sports', value: 'sports' },
    { label: 'Music', value: 'music' },
    { label: 'Travel', value: 'travel' }
  ]
}

{
  type: 'date',
  label: 'Birth Date',
  required: true,
  properties: {
    minDate: '1900-01-01',
    maxDate: '2023-12-31',
    format: 'YYYY-MM-DD'
  }
}


{
  type: 'time',
  label: 'Preferred Time',
  properties: {
    format: '24h', // or '12h'
    step: 15 // minutes
  }
}

{
  type: 'datetime',
  label: 'Appointment Time',
  required: true,
  properties: {
    minDate: '2024-01-01',
    format: 'YYYY-MM-DD HH:mm'
  }
}

{
  type: 'paragraph',
  label: 'Terms and Conditions',
  properties: {
    content: 'By submitting this form, you agree to our terms and conditions...',
    style: 'info' // 'default', 'info', 'warning', 'error'
  }
}


*/

function OptionsInput({ options, onChange }) {
  const [localOptions, setLocalOptions] = React.useState(options || [])
  const handleOptionChange = (index, value) => {
    const updatedOptions = [...localOptions]
    updatedOptions[index] = value
    setLocalOptions(updatedOptions)
    onChange(updatedOptions)
  }

  const handleAddOption = () => {
    const updatedOptions = [...localOptions, '']
    setLocalOptions(updatedOptions)
    onChange(updatedOptions)
  }
  return (
    <div>
      {localOptions.map((option, index) => (
        <>
          <TextField
            key={index}
            label={`Option ${index + 1}`}
            value={option}
            onChange={(e) => handleOptionChange(index, e.target.value)}
            style={{ marginBottom: 10, marginRight: 10 }}
          />
          <DeleteButton
            onClick={() => {
              const updatedOptions = localOptions.filter((_, i) => i !== index)
              setLocalOptions(updatedOptions)
              onChange(updatedOptions)
            }}
            disablediv={true}
          />
        </>
      ))}
      <Button variant='outlined' onClick={handleAddOption}>
        {' '}
        Add Option{' '}
      </Button>
    </div>
  )
}

function StaticTextInputs({ content, style, onChange }) {
  return (
    <>
      <TextField
        label='Static Text Content'
        multiline
        minRows={4}
        value={content}
        onChange={(e) => onChange(e.target.value)}
        fullWidth
      />
      <FormControl variant='standard' sx={{ m: 1, minWidth: 120 }}>
        <InputLabel id='style-label'>Style</InputLabel>
        <Select
          label='Style'
          value={style}
          onChange={(e) => onChange(e.target.value)}
        >
          <MenuItem value='default'>Default</MenuItem>
          <MenuItem value='info'>Info</MenuItem>
          <MenuItem value='warning'>Warning</MenuItem>
          <MenuItem value='error'>Error</MenuItem>
        </Select>
      </FormControl>
    </>
  )
}

function FormDesigner({ formdata, onChange, onSave, seasons, onClose }) {
  const [isEdited, setIsEdited] = React.useState(false)
  const [editedInputs, setEditedInputs] = React.useState(
    formdata ? formdata.inputs : []
  )
  const [localFormData, setLocalFormData] = React.useState(formdata || {})
  const handleInputChange = (index, field, value) => {
    const updatedInputs = [...editedInputs]
    updatedInputs[index] = { ...updatedInputs[index], [field]: value }
    setEditedInputs(updatedInputs)
  }

  const handleFormDataChange = (field, value) => {
    const updatedFormData = { ...localFormData, [field]: value }
    setLocalFormData(updatedFormData)
    setIsEdited(true)
    onChange(updatedFormData)
  }

  const handleAddInput = () => {
    setEditedInputs([
      ...editedInputs,
      { label: '', type: 'text', required: false },
    ])
    setIsEdited(true)
  }

  const handleSave = () => {
    localFormData.inputs = editedInputs
    onSave(localFormData)
    setIsEdited(false)
  }

  return (
    <div
      className='form-designer'
      style={{
        padding: 20,
        flexGrow: 1,
        // border: '1px solid #ccc',
        borderRadius: 4,
      }}
    >
      <Grid
        container
        spacing={2}
        direction='row'
        // component={Paper}
      >
        <Grid container size={{ xs: 12 }} component={Paper} spacing={2} sx={{ p: 2 }}>
          <Grid size={{ xs: 12 }} style={{ marginBottom: 10 }}>
            <Typography variant='h6' component={'span'}>
              Valmennushakemus / ilmoittautumislomake
            </Typography>
            <Button
              style={{ float: 'right' }}
              variant='outlined'
              color='secondary'
              onClick={onClose}
            >
              Sulje
            </Button>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              label='Lomakkeen otsikko'
              placeholder='Hakemuslomake kaudelle ...'
              value={localFormData.title || ''}
              fullWidth
              onChange={(e) => handleFormDataChange('title', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label='Ingress'
              multiline
              minRows={4}
              placeholder='Hakemuksen kuvaus'
              value={localFormData.ingress || ''}
              fullWidth
              onChange={(e) => handleFormDataChange('ingress', e.target.value)}
            />
          </Grid>
          {/* <Grid size={{ xs: 12 }}>
            <Select
              label='Kausi'
              value={localFormData.season || ''}
              onChange={(e) => handleFormDataChange('season', e.target.value)}
              fullWidth
            >
              {seasons &&
                seasons.map((season) => (
                  <MenuItem key={season.id} value={season.id}>
                    {season.name}
                  </MenuItem>
                ))}
            </Select>
          </Grid> */}
          {editedInputs.map((input, index) => (
            <Grid
              container
              size={{ xs: 12 }}
              spacing={0}
              key={index}
              component={Paper}
              justifyContent='space-between'
              style={{ padding: 10, marginTop: 10 }}
            >
              <Grid size={{ xs: 3 }}>
                <TextField
                  label='Kysymys'
                  value={input.label}
                  fullWidth
                  onChange={(e) =>
                    handleInputChange(index, 'label', e.target.value)
                  }
                />
              </Grid>
              <Grid size={{ xs: 2 }}>
                <FormControl variant='standard' sx={{ m: 1, minWidth: 120 }}>
                  <InputLabel id={`type-label-${index}`}>Tyyppi</InputLabel>
                  <Select
                    label='Type'
                    value={input.type}
                    onChange={(e) =>
                      handleInputChange(index, 'type', e.target.value)
                    }
                  >
                    <MenuItem value='text'>Teksti</MenuItem>
                    <MenuItem value='number'>Numero</MenuItem>
                    <MenuItem value='email'>Sähköposti</MenuItem>
                    <MenuItem value='phone'>Puhelin</MenuItem>
                    <MenuItem value='date'>Päivämäärä</MenuItem>
                    <MenuItem value='time'>Aika</MenuItem>
                    <MenuItem value='datetime'>Päivämäärä ja aika</MenuItem>
                    <MenuItem value='select'>Valinta</MenuItem>
                    <MenuItem value='multiselect'>
                      Valinta monta vastausta
                    </MenuItem>
                    <MenuItem value='textarea'>Tekstialue</MenuItem>
                    <MenuItem value='dropdown'>Alasvetovalikko</MenuItem>
                    <MenuItem value='radio'>Radio</MenuItem>
                    <MenuItem value='checkbox'>Valintaruutu</MenuItem>

                    <MenuItem value='paragraph'>
                      Kappale (Staattinen teksti)
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {input.type !== 'paragraph' && (
                <Grid size={{ xs: 2 }}>
                  <FormControl variant='standard' sx={{ m: 1, minWidth: 120 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name='Required'
                          color='success'
                          onChange={(e) =>
                            handleInputChange(
                              index,
                              'required',
                              e.target.checked
                            )
                          }
                        />
                      }
                      label='Pakollinen'
                    />
                  </FormControl>
                </Grid>
              )}
              {/* <Grid size={{ xs: 3 }}>
              <FormControl variant='standard' sx={{ m: 1, minWidth: 120 }}>
                <TextField
                  label='Placeholder'
                  value={input.placeholder || ''}
                  fullWidth
                  onChange={(e) =>
                    handleInputChange(index, 'placeholder', e.target.value)
                  }
                />
              </FormControl>
            </Grid> */}
              {input.type === 'select' ||
              input.type === 'multiselect' ||
              input.type === 'dropdown' ||
              input.type === 'radio' ? (
                <Grid size={{ xs: 3 }}>
                  <OptionsInput
                    options={input.options || []}
                    onChange={(options) =>
                      handleInputChange(index, 'options', options)
                    }
                  />
                </Grid>
              ) : null}
              {input.type === 'paragraph' ? (
                <Grid size={{ xs: 4 }}>
                  <StaticTextInputs
                    content={
                      input && input.properties ? input.properties.content : ''
                    }
                    style={
                      input && input.properties
                        ? input.properties.style
                        : 'default'
                    }
                    onChange={(value) =>
                      handleInputChange(index, 'properties', {
                        ...input.properties,
                        content: value,
                      })
                    }
                  />
                </Grid>
              ) : null}
              <Grid
                size={{ xs: 1 }}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'right',
                }}
              >
                <DeleteButton
                  onClick={() => {
                    const updatedInputs = editedInputs.filter(
                      (_, i) => i !== index
                    )
                    setEditedInputs(updatedInputs)
                  }}
                  disablediv={true}
                  label='Poista kysymys'
                />
              </Grid>
            </Grid>
          ))}
          <Grid size={{ xs: 12 }} style={{ padding: 10 }}>
            <Button
              variant='contained'
              color='primary'
              onClick={handleAddInput}
            >
              Lisää kysymys
            </Button>
            <Button
              disabled={!isEdited}
              variant='contained'
              color='secondary'
              onClick={handleSave}
              style={{ marginLeft: 10 }}
            >
              Tallenna
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <div className='form-preview'>
        <h4>Form Preview</h4>
        <pre>{JSON.stringify(localFormData, null, 2)}</pre>
        <pre>{JSON.stringify(editedInputs, null, 2)}</pre>
      </div>
    </div>
  )
}

export default FormDesigner
